/**
 * Перевод формы повторения из UI в контракт `/v2/tasks/{id}/recurrence`.
 *
 * В v2-спеке у `frequency` тип просто `string`, без перечисления значений.
 * Берём их из контракта того же бэка на legacy-маршруте `/recurrences`, где они
 * задокументированы явно: `DAILY`, `WEEKLY`, `MONTHLY`, дни недели 1=Пн…7=Вс,
 * `monthDay` 1–31. Поля v2 (`frequency`, `interval`, `weekDays`, `monthDay`)
 * совпадают с legacy один в один — отличаются только имена дат
 * (`startAt`/`endAt` вместо `startDate`/`endDate`).
 *
 * Если бэк ждёт другие литералы, править нужно здесь одно место.
 */
import type { RecurrenceCreateV2 } from '../api/task-automation-v2';

/** Период, которым оперирует форма (`DRecurrence`). */
export type RecurrencePeriodV2 = 'day' | 'week' | 'month';

const FREQUENCY: Record<RecurrencePeriodV2, string> = {
  day: 'DAILY',
  week: 'WEEKLY',
  month: 'MONTHLY',
};

/** Обратный разбор — чтобы показать существующее правило в той же форме. */
export function periodOfFrequency(frequency?: string): RecurrencePeriodV2 {
  const upper = (frequency ?? '').toUpperCase();
  if (upper === 'WEEKLY') {
    return 'week';
  }
  if (upper === 'MONTHLY') {
    return 'month';
  }

  return 'day';
}

export interface RecurrenceFormV2 {
  period: RecurrencePeriodV2;
  /** Первый запуск, значение из `datetime-local` («YYYY-MM-DDTHH:mm»). */
  firstAt: string;
  /** Каждые N периодов. */
  everyCount: number;
  /** Дата окончания; пусто — повторять бесконечно. */
  untilAt: string;
  responsibleId?: string;
}

/** «YYYY-MM-DDTHH:mm» → RFC3339. Пустая строка даёт undefined. */
function rfc3339(local: string): string | undefined {
  if (!local.trim()) {
    return undefined;
  }
  const d = new Date(local);

  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Собирает тело запроса. Возвращает `null`, когда обязательного первого запуска
 * нет — вызывающий код тогда просто не создаёт правило.
 */
export function buildRecurrencePayload(
  form: RecurrenceFormV2
): RecurrenceCreateV2 | null {
  const startAt = rfc3339(form.firstAt);
  if (!startAt) {
    return null;
  }

  const payload: RecurrenceCreateV2 = {
    frequency: FREQUENCY[form.period],
    startAt,
  };

  if (form.everyCount > 1) {
    payload.interval = form.everyCount;
  }
  const endAt = rfc3339(form.untilAt);
  if (endAt) {
    payload.endAt = endAt;
  }
  if (form.responsibleId) {
    payload.responsibleId = form.responsibleId;
  }
  /* Для недельного и месячного повтора бэк ждёт, в какие именно дни повторять.
   * Форма таких полей не показывает, поэтому берём их из даты первого запуска:
   * «каждую неделю в этот день», «каждый месяц этого числа». */
  const start = new Date(startAt);
  if (form.period === 'week') {
    // getDay(): 0=Вс…6=Сб, бэк ждёт 1=Пн…7=Вс.
    payload.weekDays = [start.getDay() === 0 ? 7 : start.getDay()];
  }
  if (form.period === 'month') {
    payload.monthDay = start.getDate();
  }

  return payload;
}
